<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { extractMediaBankEntryIdsFromHtml } from './media-lookup';

const props = defineProps({
    contentId: { type: String, required: true },
    pageTitle: { type: String, required: true },
    containerType: { type: String, required: true },
    containerRef: { type: String, default: '' },
    initialHtml: { type: String, default: '' },
    saveUrl: { type: String, required: true },
    updateHtmlUrl: { type: String, required: true },
    mediaBankUrl: { type: String, default: '' },
    resolveMediaUrl: { type: String, default: '' },
    authToken: { type: String, default: '' },
    viewmode: { type: String, default: 'default' },
});

const componentLibraryRef = ref(null);
const currentHtml = ref(props.initialHtml);
const isSaving = ref(false);
const savingState = ref('');
const imagePickerVisible = ref(false);
const editingImage = ref(null);
const mediaUrlMap = ref({});
const mediaEntryMap = ref({});
const previousAuthorizationHeader = ref(undefined);

const notifyHost = (type, payload = {}) => {
    const message = { type, ...payload };

    if (window.webkit?.messageHandlers?.aiPageBuilder) {
        window.webkit.messageHandlers.aiPageBuilder.postMessage(message);
    }

    if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, '*');
    }
};

const syncHtml = (html) => {
    currentHtml.value = html;
    notifyHost('html-updated', { html });
};

const persistHtml = (html) => {
    axios.post(props.updateHtmlUrl, { html }).catch(() => {});
};

const handleHtmlUpdate = (newHtml) => {
    syncHtml(newHtml);
    persistHtml(newHtml);
};

const handleComponentInsert = (componentHtml) => {
    const updatedHtml = `${currentHtml.value}\n${componentHtml}`;
    syncHtml(updatedHtml);
    persistHtml(updatedHtml);
};

const handleComponentEdit = ({ componentType, config, index }) => {
    componentLibraryRef.value?.editComponent(componentType, config, index);
    notifyHost('component-clicked', { componentType, config, index });
};

const handleComponentUpdate = ({ type, config, index }) => {
    let count = 0;
    const updatedHtml = currentHtml.value.replace(/<mf-component\s[^>]*>/g, (match) => {
        if (count++ === index) {
            return `<mf-component type="${type}" config='${config}'>`;
        }

        return match;
    });

    syncHtml(updatedHtml);
    persistHtml(updatedHtml);
};

const handleComponentDelete = ({ index }) => {
    if (typeof document === 'undefined') {
        return;
    }

    const container = document.createElement('div');
    container.innerHTML = currentHtml.value || '';
    const component = container.querySelectorAll('mf-component')[index];

    if (!component) {
        return;
    }

    component.remove();
    const updatedHtml = container.innerHTML;
    syncHtml(updatedHtml);
    persistHtml(updatedHtml);
};

const handleImageEdit = (info) => {
    editingImage.value = info;
    imagePickerVisible.value = true;
    notifyHost('image-clicked', info);
};

const resolveMediaIds = async () => {
    if (!props.resolveMediaUrl) {
        return;
    }

    const ids = extractMediaBankEntryIdsFromHtml(currentHtml.value);

    if (ids.length === 0) {
        mediaUrlMap.value = {};
        mediaEntryMap.value = {};

        return;
    }

    const missingIds = ids.filter((id) => !mediaEntryMap.value[id] && !mediaUrlMap.value[id]);

    if (missingIds.length === 0) {
        return;
    }

    try {
        const res = await axios.post(props.resolveMediaUrl, {
            entry_ids: missingIds.map((id) => parseInt(id, 10)),
        });
        mediaUrlMap.value = { ...mediaUrlMap.value, ...(res.data.urls || {}) };
        mediaEntryMap.value = { ...mediaEntryMap.value, ...(res.data.entries || {}) };
    } catch (e) {
        // Silently fail
    }
};

const handleImageSelect = ({ src, alt, mediaId }) => {
    if (editingImage.value === null) {
        return;
    }

    const targetIndex = editingImage.value.index;
    let imgCount = 0;

    const updatedHtml = currentHtml.value.replace(/<img([^>]*?)>/g, (match, attrs) => {
        if (imgCount++ !== targetIndex) {
            return match;
        }

        const classMatch = attrs.match(/class="([^"]*)"/);
        const existingClasses = classMatch ? classMatch[1] : '';

        let newAttrs = '';
        if (existingClasses) {
            newAttrs += ` class="${existingClasses}"`;
        }
        newAttrs += ` alt="${alt.replace(/"/g, '&quot;')}"`;

        if (mediaId) {
            newAttrs += ` data-media-id="${mediaId}" src=""`;
        } else {
            newAttrs += ` src="${src.replace(/"/g, '&quot;')}"`;
        }

        return `<img${newAttrs}>`;
    });

    syncHtml(updatedHtml);
    persistHtml(updatedHtml);

    imagePickerVisible.value = false;
    editingImage.value = null;

    if (mediaId) {
        resolveMediaIds();
    }
};

const handleSave = async () => {
    if (isSaving.value) {
        return;
    }

    isSaving.value = true;
    savingState.value = 'waiting';

    try {
        await axios.post(props.saveUrl, { html: currentHtml.value });
        savingState.value = 'success';
        notifyHost('saved', { html: currentHtml.value });
        setTimeout(() => {
            savingState.value = '';
        }, 2000);
    } catch (e) {
        savingState.value = 'error';
        notifyHost('save-failed');
    } finally {
        isSaving.value = false;
    }
};

const handleHostMessage = (event) => {
    const message = event.data;
    if (!message || typeof message !== 'object') {
        return;
    }

    if (message.type === 'ai-page-builder:set-html' && typeof message.html === 'string') {
        syncHtml(message.html);
        resolveMediaIds();
        persistHtml(message.html);
    }

    if (message.type === 'ai-page-builder:save') {
        handleSave();
    }

    if (message.type === 'ai-page-builder:insert-component' && typeof message.html === 'string') {
        handleComponentInsert(message.html);
    }

    if (
        message.type === 'ai-page-builder:update-component'
        && typeof message.componentType === 'string'
        && typeof message.config === 'string'
        && typeof message.index === 'number'
    ) {
        handleComponentUpdate({
            type: message.componentType,
            config: message.config,
            index: message.index,
        });
    }
};

onMounted(() => {
    if (props.authToken) {
        previousAuthorizationHeader.value = axios.defaults.headers.common.Authorization;
        axios.defaults.headers.common.Authorization = `Bearer ${props.authToken}`;
    }

    window.aiPageBuilder = {
        getHtml: () => currentHtml.value,
        setHtml: (html) => {
            if (typeof html !== 'string') {
                return;
            }

            syncHtml(html);
            resolveMediaIds();
            persistHtml(html);
        },
        save: () => handleSave(),
        insertComponent: (html) => {
            if (typeof html === 'string') {
                handleComponentInsert(html);
            }
        },
        updateComponent: (componentType, config, index) => {
            if (typeof componentType !== 'string' || typeof config !== 'string' || typeof index !== 'number') {
                return;
            }

            handleComponentUpdate({
                type: componentType,
                config,
                index,
            });
        },
    };

    resolveMediaIds();
    notifyHost('ready', {
        contentId: props.contentId,
        html: currentHtml.value,
    });
    window.addEventListener('message', handleHostMessage);
});

onUnmounted(() => {
    window.removeEventListener('message', handleHostMessage);
    delete window.aiPageBuilder;

    if (props.authToken) {
        if (previousAuthorizationHeader.value) {
            axios.defaults.headers.common.Authorization = previousAuthorizationHeader.value;
        } else {
            delete axios.defaults.headers.common.Authorization;
        }
    }
});
</script>

<template>
    <div class="flex h-dvh flex-col bg-gray-100">
        <div
            v-if="props.viewmode === 'default'"
            class="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 shadow-sm"
        >
            <div class="min-w-0">
                <p class="truncate text-sm font-medium text-gray-800">{{ pageTitle }}</p>
            </div>

            <button-base
                style-type="success"
                @click="handleSave"
                :state="savingState"
                class="ml-4"
            >
                <mf-translate domainkey="page:pagebuilder.save"></mf-translate>
            </button-base>
        </div>

        <div class="flex min-h-0 flex-1 flex-col">
            <div
                class="min-h-0 flex-1"
                :class="props.viewmode === 'app-native' ? '' : 'p-4'"
            >
                <ai-page-builder-preview
                    :html="currentHtml"
                    :device-mode="'desktop'"
                    :media-url-map="mediaUrlMap"
                    :media-entry-map="mediaEntryMap"
                    :container-type="containerType"
                    :container-ref="containerRef"
                    :viewmode="props.viewmode"
                    @update-html="handleHtmlUpdate"
                    @edit-component="handleComponentEdit"
                    @edit-image="handleImageEdit"
                ></ai-page-builder-preview>
            </div>

            <ai-page-builder-component-library
                ref="componentLibraryRef"
                class="flex-shrink-0 border-t border-gray-200 bg-white"
                @insert="handleComponentInsert"
                @update="handleComponentUpdate"
                @remove="handleComponentDelete"
            ></ai-page-builder-component-library>
        </div>

        <ai-page-builder-image-picker
            :visible="imagePickerVisible"
            :current-src="editingImage?.currentSrc || ''"
            :current-alt="editingImage?.currentAlt || ''"
            :media-id="editingImage?.mediaId || null"
            :media-bank-url="mediaBankUrl"
            @select="handleImageSelect"
            @close="imagePickerVisible = false"
        ></ai-page-builder-image-picker>
    </div>
</template>
